/**
 * Single source of truth for Sage's action-tag → app-navigation mapping.
 *
 * Sage's responses contain markers like `[GO_TO_CALM_CYCLE]` so the frontend
 * can intercept them, render clickable "tool links" inline with the message,
 * and (optionally) auto-navigate the user to that tool.
 *
 * Add new tools here ONCE — both AIGuide.jsx and UnifiedHome.jsx (and any
 * future surface) read from this same map.
 */

export const ACTION_TAG_MAP = {
    // ─── Check-in / Assessment ──────────────────────────────────
    GO_TO_CHECK_IN: { route: '/initial-assessment', name: 'Quick Check-In' },
    START_INITIAL_ASSESSMENT: { route: '/initial-assessment', name: 'Assessment' },
    GO_TO_ASSESSMENT: { route: '/initial-assessment', name: 'Assessment' },

    // ─── Regulation Tools ───────────────────────────────────────
    GO_TO_STILL_SPHERE: { route: '/regulate/still-sphere', name: 'Still Sphere' },
    GO_TO_CALM_CYCLE: { route: '/regulate/breathing-orb', name: 'Calm Cycle' },
    GO_TO_MICRO_RESET: { route: '/regulate/micro-reset', name: 'Micro Reset' },
    GO_TO_MICRO_RESET_BREATH: { route: '/regulate/micro-reset?focus=breath', name: 'Micro Reset (Breathing)' },
    GO_TO_MICRO_RESET_SOMATIC: { route: '/regulate/micro-reset?focus=somatic', name: 'Micro Reset (Body)' },
    GO_TO_MICRO_RESET_GROUNDING: { route: '/regulate/micro-reset?focus=grounding', name: 'Micro Reset (Grounding)' },
    GO_TO_MICRO_RESET_MOVEMENT: { route: '/regulate/micro-reset?focus=movement', name: 'Micro Reset (Movement)' },
    GO_TO_MICRO_RESET_MINDFUL: { route: '/regulate/micro-reset?focus=mindful', name: 'Micro Reset (Mindful)' },
    GO_TO_NAVIGATOR: { route: '/regulate/regulation-navigator', name: 'Regulation Navigator' },
    GO_TO_BOX_BREATHING: { route: '/regulate/box-breathing', name: 'Box Breathing' },
    GO_TO_BODY_SCAN: { route: '/regulate/body-scan', name: 'Body Scan' },
    GO_TO_PMR: { route: '/regulate/pmr', name: 'Progressive Muscle Relaxation' },
    GO_TO_SOMATIC: { route: '/regulate/somatic', name: 'Somatic Practices' },
    GO_TO_GUIDED_MEDITATIONS: { route: '/regulate/guided-meditations', name: 'Guided Meditations' },
    GO_TO_RSD_MEDITATION: { route: '/regulate/guided-meditations?meditation=rsd-healing', name: 'RSD Meditation' },
    GO_TO_CALMING_ANXIETY_MEDITATION: { route: '/regulate/guided-meditations?meditation=calming-anxiety', name: 'Calming Anxiety Meditation' },
    GO_TO_SELF_TALK: { route: '/regulate/self-talk', name: 'Self-Talk Reframes' },
    GO_TO_EMDR: { route: '/regulate/emdr', name: 'Bilateral Regulation' },
    GO_TO_EMDR_VISUAL: { route: '/regulate/emdr?mode=visual', name: 'Bilateral (Visual)' },
    GO_TO_EMDR_AUDIO: { route: '/regulate/emdr?mode=audio', name: 'Bilateral (Audio)' },
    GO_TO_REGULATION_TOOLS: { route: '/regulate', name: 'Regulation Tools' },
    GO_TO_HYPNOTHERAPY: { route: '/hypnotherapy', name: 'Hypnotherapy' },

    // ─── Breathing Patterns ─────────────────────────────────────
    GO_TO_BREATHING_PATTERNS: { route: '/regulate/breathing-patterns', name: 'Breathing Patterns' },
    GO_TO_BREATHING_PHYSIOLOGICAL_SIGH: { route: '/regulate/breathing-patterns?pattern=physiological-sigh', name: 'Physiological Sigh' },
    GO_TO_BREATHING_478: { route: '/regulate/breathing-patterns?pattern=4-7-8', name: '4-7-8 Breathing' },
    GO_TO_BREATHING_BOX: { route: '/regulate/box-breathing', name: 'Box Breathing' },
    GO_TO_BREATHING_EXTENDED_EXHALE: { route: '/regulate/breathing-patterns?pattern=extended-exhale', name: 'Extended Exhale' },
    GO_TO_BREATHING_COHERENT: { route: '/regulate/breathing-patterns?pattern=coherent', name: 'Coherent Breathing' },

    // ─── Relationship Hub ───────────────────────────────────────
    GO_TO_RELATIONSHIP_HUB: { route: '/relationship-hub', name: 'Relationship Hub' },
    GO_TO_RELATIONSHIP_CHECK_IN: { route: '/relationship-hub/check-in', name: 'Relationship Check-In' },
    GO_TO_BREAK_THE_CYCLE: { route: '/relationship-hub/break-the-cycle', name: 'Break the Cycle' },
    GO_TO_WHICH_CYCLE: { route: '/relationship-hub/cycles', name: 'Which Cycle Are We In?' },
    GO_TO_ATTACHMENT_NEEDS: { route: '/relationship-hub/attachment-needs', name: 'Attachment Needs' },
    GO_TO_SOLO_NEED: { route: '/relationship-hub/solo-need', name: 'What Do I Need?' },
    GO_TO_REPAIR: { route: '/relationship-hub/repair', name: 'Repair & Reconnection' },
    GO_TO_RELATIONSHIP_SAFETY: { route: '/regulate/relationship-check', name: 'Relationship Safety Check' },
    GO_TO_COREGULATION: { route: '/regulate/coregulation', name: 'Co-Regulation' },
    GO_TO_PARTNER_MODE: { route: '/regulate/partner-mode', name: 'Partner Mode' },
    GO_TO_PARTNER_SUPPORT: { route: '/regulate/adhd-partner-support', name: 'Partner Support Guide' },
    GO_TO_ADHD_TRIGGERS: { route: '/adhd-relationships', name: 'ADHD Relational Triggers' },
    GO_TO_INFINITY_LOOP: { route: '/relationship-hub/infinity-loop', name: 'Infinity Loop' },

    // ─── Settling aliases ───────────────────────────────────────
    GO_TO_SETTLE_STILL_SPHERE: { route: '/regulate/still-sphere', name: 'Still Sphere' },
    GO_TO_SETTLE_CALM_CYCLE: { route: '/regulate/breathing-orb', name: 'Calm Cycle' },
    GO_TO_SETTLE_MICRO_RESET: { route: '/regulate/micro-reset', name: 'Micro Reset' },
    GO_TO_SETTLE_BOX_BREATHING: { route: '/regulate/box-breathing', name: 'Box Breathing' },
    GO_TO_SETTLE_NAVIGATOR: { route: '/regulate/regulation-navigator', name: 'Navigator' },

    // ─── Crisis ─────────────────────────────────────────────────
    GO_TO_CRISIS: { route: '/crisis', name: 'Crisis Resources' },
};

const ACTION_TAG_REGEX = /\[([A-Z_]+)\]/g;
const ACTION_TAG_STRIP_REGEX = /\s*\[[A-Z_]+\]\s*/g;

/**
 * Parse a Sage message and extract:
 *  - `text`    : the message with all action tags removed
 *  - `actions` : an array of `{ tag, route, name }` for each known tag found
 *
 * Tags not present in `ACTION_TAG_MAP` are stripped from text but ignored
 * (no broken links shown to the user).
 */
export function parseMessageWithActions(content) {
    if (!content) return { text: '', actions: [] };

    const actions = [];
    let match;
    // Reset regex state — `.exec` with /g is stateful
    ACTION_TAG_REGEX.lastIndex = 0;
    while ((match = ACTION_TAG_REGEX.exec(content)) !== null) {
        const tag = match[1];
        if (ACTION_TAG_MAP[tag]) {
            actions.push({ tag, ...ACTION_TAG_MAP[tag] });
        }
    }

    const text = content.replace(ACTION_TAG_STRIP_REGEX, ' ').trim();
    return { text, actions };
}
